import { ReactElement } from "react"
import { HotelQuote, LivePrice, QuoteLoyalty } from "@/types/price-quote"

import { formatHotelProviderName } from "@/lib/hotel-utils"
import { Locale } from "@/lib/locale-hooks"
import {
  QuotePointsBreakdown,
  shownLoyalty,
} from "@/components/hotel-quote-points"
import IconAlert from "@/components/icons/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Separator } from "./ui/separator"

interface PriceBreakdownPopoverProps {
  quote: HotelQuote
  livePriceNumber?: number
  liveCurrency?: string
  livePrice?: LivePrice
  priceChangedSignificantly: boolean
  quoteDatesInPast?: boolean
  /** Reason when live price shows room/hotel unavailable */
  unavailableReason?: string
  /** Points math for the stay (the live price hook's `loyalty`); adds a Points tab when the traveler cares about points */
  loyaltyFacts?: QuoteLoyalty
  children: ReactElement
}

export function PriceBreakdownPopover({
  quote,
  livePriceNumber,
  liveCurrency,
  livePrice,
  priceChangedSignificantly,
  quoteDatesInPast,
  unavailableReason,
  loyaltyFacts,
  children,
}: PriceBreakdownPopoverProps) {
  const formatMoney = Locale.useFormatMoney()
  // already locale-formatted; wrap here so Weglot can't mint an entry per amount
  const formatPrice = (amount: number, currency = "USD") => (
    <span className="no-translate">
      {formatMoney(amount, false, currency || "USD")}
    </span>
  )

  if (quote.points) {
    return (
      <PointsQuoteBreakdown
        quote={quote}
        points={quote.points}
        loyaltyFacts={loyaltyFacts}
        formatPrice={formatPrice}
      >
        {children}
      </PointsQuoteBreakdown>
    )
  }

  const { nights } = quote.dates
  const nightsLabel = nights === 1 ? "night" : "nights"
  const currency = quote.price.currency
  const numRooms = quote.people.num_rooms ?? 1
  const isMultiRoom = numRooms > 1

  // Per-room prices from quote
  const perRoomPerNight = quote.price.per_night
  const perRoomTotal = quote.price.total

  // Total prices across all rooms
  const totalPerNight = perRoomPerNight * numRooms
  const grandTotal = perRoomTotal * numRooms

  const hasTaxesAndFees = quote.price.total_with_fees != null
  // Taxes and fees also need to be multiplied by num_rooms
  const taxesAndFeesAmount =
    hasTaxesAndFees && quote.price.total_with_fees != null
      ? (quote.price.total_with_fees - quote.price.total) * numRooms
      : null
  const grandTotalWithFees =
    quote.price.total_with_fees != null
      ? quote.price.total_with_fees * numRooms
      : null

  // Check if live price differs from quoted (any amount, not just significant)
  // livePriceNumber is per-room, compare against per-room quoted price
  const livePriceDiffers =
    livePriceNumber !== undefined &&
    Math.round(livePriceNumber) !== Math.round(perRoomPerNight)

  // Check if live price is lower (better deal)
  const livePriceLower =
    livePriceDiffers &&
    livePriceNumber !== undefined &&
    livePriceNumber < perRoomPerNight

  // Live prices scaled to all rooms
  const liveTotalPerNight =
    livePriceNumber !== undefined ? livePriceNumber * numRooms : undefined
  const liveGrandTotal =
    livePriceNumber !== undefined
      ? livePriceNumber * nights * numRooms
      : undefined

  // Live all-in total (taxes + fees) when the live fetch reports fees — the
  // honest "what you'll pay" figure, preferred over the quoted all-in.
  const liveTotalWithFees =
    livePriceDiffers &&
    livePrice?.total_with_fees != null &&
    livePrice?.total != null
      ? livePrice.total_with_fees * numRooms
      : null
  // All-in total actually shown as "Total" (live preferred), or null with no fees.
  const effectiveTotalWithFees = liveTotalWithFees ?? grandTotalWithFees

  const cash = (
    <>
      {/* Price breakdown - label as "quoted" when live price differs */}
      {livePriceDiffers && (
        <div className="mb-1.5 text-xs">Originally quoted</div>
      )}
      <div className="space-y-1.5">
        {/* Multi-room breakdown: show per-room rate first */}
        {isMultiRoom && (
          <div className="flex items-center justify-between gap-4">
            <span>
              <span className="no-translate">{numRooms}</span> rooms x{" "}
              {livePriceDiffers && livePriceNumber !== undefined ? (
                <>
                  <span className="no-translate line-through">
                    {formatPrice(perRoomPerNight, currency)}
                  </span>{" "}
                  <span className="no-translate">
                    {formatPrice(livePriceNumber, liveCurrency ?? currency)}
                  </span>
                </>
              ) : (
                <span className="no-translate">
                  {formatPrice(perRoomPerNight, currency)}
                </span>
              )}
            </span>
            <span className="font-medium">
              {livePriceDiffers && liveTotalPerNight !== undefined ? (
                <>
                  <span className="no-translate font-normal line-through">
                    {formatPrice(totalPerNight, currency)}
                  </span>{" "}
                  <span className="no-translate">
                    {formatPrice(liveTotalPerNight, liveCurrency ?? currency)}
                  </span>
                  <span className="text-muted">/night</span>
                </>
              ) : (
                <>
                  <span className="no-translate">
                    {formatPrice(totalPerNight, currency)}
                  </span>
                  <span className="text-muted">/night</span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Nights calculation */}
        <div className="flex items-center justify-between gap-4">
          <span>
            <span className="no-translate">{nights}</span> {nightsLabel} x{" "}
            {livePriceDiffers ? (
              <>
                <span className="no-translate line-through">
                  {formatPrice(
                    isMultiRoom ? totalPerNight : perRoomPerNight,
                    currency
                  )}
                </span>{" "}
                <span className="no-translate">
                  {formatPrice(
                    isMultiRoom
                      ? (liveTotalPerNight ?? totalPerNight)
                      : (livePriceNumber ?? perRoomPerNight),
                    liveCurrency ?? currency
                  )}
                </span>
              </>
            ) : (
              <span className="no-translate">
                {formatPrice(
                  isMultiRoom ? totalPerNight : perRoomPerNight,
                  currency
                )}
              </span>
            )}
          </span>
          <span className="font-medium">
            {livePriceDiffers && liveGrandTotal !== undefined ? (
              <>
                <span className="no-translate font-normal line-through">
                  {formatPrice(grandTotal, currency)}
                </span>{" "}
                <span className="no-translate">
                  {formatPrice(liveGrandTotal, liveCurrency ?? currency)}
                </span>
              </>
            ) : (
              <span className="no-translate">
                {formatPrice(grandTotal, currency)}
              </span>
            )}
          </span>
        </div>

        {hasTaxesAndFees && taxesAndFeesAmount !== null ? (
          (() => {
            // When live price differs and includes fees, show updated taxes & total
            const liveTaxesAndFees =
              liveTotalWithFees != null && liveGrandTotal != null
                ? liveTotalWithFees - liveGrandTotal
                : null
            const effectiveTaxes = liveTaxesAndFees ?? taxesAndFeesAmount
            const effectiveTotal = liveTotalWithFees ?? grandTotalWithFees
            const effectiveTotalCurrency =
              liveTotalWithFees != null ? (liveCurrency ?? currency) : currency

            return (
              <>
                <div className="flex items-center justify-between gap-4">
                  <span className="opacity-70">Taxes & fees</span>
                  <span className="no-translate font-medium">
                    +{formatPrice(effectiveTaxes, effectiveTotalCurrency)}
                  </span>
                </div>

                <Separator className="my-1.5" />

                <div className="flex items-center justify-between gap-4 font-semibold">
                  <span>Total</span>
                  <span className="no-translate">
                    {formatPrice(effectiveTotal ?? 0, effectiveTotalCurrency)}
                  </span>
                </div>

                {/* Bridge the total back to a per-night figure so it squares
                      with the all-in price shown on the card — no mental math. */}
                {nights > 1 && (
                  <div className="text-right text-xs text-muted">
                    <span className="no-translate">
                      {formatPrice(
                        (effectiveTotal ?? 0) / nights,
                        effectiveTotalCurrency
                      )}
                    </span>
                    {isMultiRoom ? (
                      <span>/night total all-in</span>
                    ) : (
                      <span>/night all-in</span>
                    )}
                  </div>
                )}
              </>
            )
          })()
        ) : (
          <div className="mt-1 text-xs text-muted italic">
            Taxes & fees calculated at checkout
          </div>
        )}
      </div>

      {/* Current price when different from quoted */}
      {livePriceDiffers && livePriceNumber !== undefined && (
        <>
          <Separator className="my-3" />
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              {priceChangedSignificantly && !livePriceLower && (
                <IconAlert className="size-4 shrink-0 text-yellow-11" />
              )}
              <span>Current price</span>
            </span>
            <span>
              <span className="font-medium">
                {formatPrice(
                  isMultiRoom ? (liveTotalPerNight ?? 0) : livePriceNumber,
                  liveCurrency ?? currency
                )}
              </span>
              <span className="text-sm">
                {isMultiRoom ? <span>/night total</span> : <span>/night</span>}
              </span>
            </span>
          </div>
        </>
      )}

      {/* Budget comparison - compare against total nightly rate (all rooms) */}
      {quote.budget_per_night && (
        <>
          {(() => {
            // Compare the budget against the ALL-IN nightly rate (taxes +
            // fees) — the same figure shown as "Total" — so the verdict can
            // never contradict the breakdown. Only fall back to the base rate
            // when we genuinely have no fee data, and say so. Skip entirely
            // when currencies don't match.
            const budgetCurrency = quote.budget_per_night.currency ?? currency
            const hasAllIn = effectiveTotalWithFees != null
            const usingLive =
              liveTotalWithFees != null ||
              (!hasAllIn && liveTotalPerNight != null)
            const rateCurrency = usingLive
              ? (liveCurrency ?? currency)
              : currency
            if (budgetCurrency !== rateCurrency) return null

            const nightlyRate = hasAllIn
              ? effectiveTotalWithFees / nights
              : (liveTotalPerNight ?? totalPerNight)
            const qualifier = hasAllIn ? " all-in" : " before taxes & fees"
            const diff = quote.budget_per_night.price - nightlyRate

            if (diff > 0) {
              return (
                <div className="mt-1 text-xs text-green-11">
                  Under budget by {formatPrice(diff, currency)}
                  /night{qualifier}
                </div>
              )
            } else if (diff < 0) {
              return (
                <div className="mt-1 text-xs text-yellow-11">
                  Over budget by {formatPrice(Math.abs(diff), currency)}
                  /night{qualifier}
                </div>
              )
            }
            return (
              <div className="mt-1 text-xs text-muted">
                {`Exactly on budget${qualifier}`}
              </div>
            )
          })()}
        </>
      )}
      {/* Past dates, unavailability, or room-level disclaimers */}
      {quoteDatesInPast ? (
        <div className="mt-3 text-xs text-muted">
          This price was quoted for dates that have now passed.
        </div>
      ) : unavailableReason === "room_unavailable" ? (
        <div className="mt-3 text-xs text-muted">
          This specific room is no longer available at this price.
        </div>
      ) : unavailableReason === "hotel_unavailable" ? (
        <div className="mt-3 text-xs text-muted">
          This hotel has no availability for these dates.
        </div>
      ) : unavailableReason === "fetch_failed" ? (
        <div className="mt-3 text-xs text-muted">
          Current price could not be verified.
        </div>
      ) : (
        quote.room !== null &&
        !livePriceNumber && (
          <div className="mt-3 text-xs text-muted">
            Price is from when this room was quoted and may have changed.
          </div>
        )
      )}
    </>
  )
  const loyalty = shownLoyalty(loyaltyFacts)

  return (
    <Popover>
      <PopoverTrigger render={children} />
      <PopoverContent side="top" size="md">
        <h2 className="font-semibold">
          {quote.room?.name ? (
            <span key="room" className="no-translate">
              {quote.room.name}
            </span>
          ) : (
            <span key="lowest">Lowest available</span>
          )}
          {quote.people.num_rooms && quote.people.num_rooms > 1 && (
            <span> ({quote.people.num_rooms} rooms)</span>
          )}
          <span> via </span>
          <span>{formatHotelProviderName(quote.provider)}</span>
        </h2>

        {loyalty ? (
          <Tabs defaultValue="cash" className="mt-2">
            <TabsList className="text-sm">
              <TabsTrigger value="cash">Cash</TabsTrigger>
              <TabsTrigger value="points">Points</TabsTrigger>
            </TabsList>
            <TabsContent value="cash" className="pt-3">
              {cash}
            </TabsContent>
            <TabsContent value="points" className="pt-3">
              <QuotePointsBreakdown
                loyalty={loyalty}
                provider={quote.provider}
                compact
              />
            </TabsContent>
          </Tabs>
        ) : (
          <>
            <Separator className="my-3" />
            {cash}
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * A points quote, a rate read on the hotel's own site: its points with their fee and terms first, the loyalty facts
 * that weigh them, and on the Cash tab the hotel's own cash rate for the same stay.
 */
function PointsQuoteBreakdown({
  quote,
  points,
  loyaltyFacts,
  formatPrice,
  children,
}: {
  quote: HotelQuote
  points: NonNullable<HotelQuote["points"]>
  loyaltyFacts?: QuoteLoyalty
  formatPrice: (amount: number, currency?: string) => ReactElement
  children: ReactElement
}) {
  const { nights } = quote.dates
  const nightsLabel = nights === 1 ? "night" : "nights"
  const loyalty = shownLoyalty(loyaltyFacts)
  const pointsPart = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-4">
        <span>
          <span className="no-translate">{nights}</span> {nightsLabel}
        </span>
        <span className="no-translate font-medium">
          {points.per_stay.toLocaleString()} pts
        </span>
      </div>
      {points.fee && (
        <div className="text-xs text-muted">Plus in cash: {points.fee}</div>
      )}
      {points.cancellation && (
        <div className="text-xs text-muted">{points.cancellation}</div>
      )}
      {loyalty && (
        <>
          <Separator className="my-3" />
          <QuotePointsBreakdown
            loyalty={loyalty}
            provider={quote.provider}
            compact
          />
        </>
      )}
    </div>
  )
  const cash = points.cash && (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-4">
        <span>
          <span className="no-translate">{nights}</span> {nightsLabel} x{" "}
          {formatPrice(points.cash.per_night, points.cash.currency)}
        </span>
        <span>{formatPrice(points.cash.total, points.cash.currency)}</span>
      </div>
      <div className="text-xs text-muted">
        {points.cash.rate_name ? (
          <span className="no-translate">{points.cash.rate_name}, </span>
        ) : null}
        <span>
          {`before taxes, on the hotel's own site for the same dates${points.cash.refundable ? "; refundable" : ""}.`}
        </span>
      </div>
    </div>
  )

  return (
    <Popover>
      <PopoverTrigger render={children} />
      <PopoverContent side="top" size="md">
        <h2 className="font-semibold">
          {points.room_name ? (
            <span className="no-translate">{points.room_name}</span>
          ) : (
            <span>Points stay</span>
          )}
          <span> via </span>
          <span>{formatHotelProviderName(quote.provider)}</span>
        </h2>
        {cash ? (
          <Tabs defaultValue="points" className="mt-2">
            <TabsList className="text-sm">
              <TabsTrigger value="points">Points</TabsTrigger>
              <TabsTrigger value="cash">Cash</TabsTrigger>
            </TabsList>
            <TabsContent value="points" className="pt-3">
              {pointsPart}
            </TabsContent>
            <TabsContent value="cash" className="pt-3">
              {cash}
            </TabsContent>
          </Tabs>
        ) : (
          <>
            <Separator className="my-3" />
            {pointsPart}
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
